import React, { useEffect, useState } from 'react'
import { Breadcrumb, Col, Container, Form, Row, Spinner, Tab } from 'react-bootstrap'
import buttonStyles from '../../../components/Button/Button.module.scss'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { Button } from '../../../components/Button/Button'
import { ReactComponent as PlusDark } from '../../../assets/images/plus-dark.svg'
import { ReactComponent as Plus } from '../../../assets/images/plus-icon.svg'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import { useGetFeatureByIdQuery } from '../../../store/ai/ai'
import { CreateScorecard, useCreateScorecardMutation, useGetAllScorecardsQuery, useUpdateScorecardMutation } from '../../../store/ai/scorecard'
import { Scorecard, ScorecardType } from '../../../models/Scorecard'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import { Divider } from '../../../components/Divider/Divider'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { useGetMeQuery } from '../../../store/user/user'
import { CrmMappingMultiFieldModal, UnmappedFieldsModal } from './CrmField'
import { interleaveWithSeparator, DEFAULT_SEPARATOR, toCamelCase } from './utils'
import { AiFeatureIntegrationInfo, CRMFieldMappings, FieldDescriptor } from '../../../models/AiFeature'
import { selectImportedForm } from '../../../store/formImport/importedForm'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { IntegrationServiceId } from '../../../common/integrations'

import './EditAIFormView.scss'
import { Tabs } from '../../../components/Tabs/Tabs'
import { FormTemplatesTab } from './FormTemplatesTab'

import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

const FIELD_TYPE_CONFIG = {
  date: {
    baseType: 'string',
    pattern: '^(0[1-9]|1[0-2])/(0[1-9]|[12][0-9]|3[01])/\\d{4}$',
    uiWidget: 'USDateInputWidget',
  },
  itin: {
    baseType: 'string',
    pattern: '^\\d{3}-\\d{2}-\\d{4}$',
    uiWidget: 'ITINInputWidget',
  },
  ssn: {
    baseType: 'string',
    pattern: '^\\d{3}-\\d{2}-\\d{4}$',
    uiWidget: 'SSNInputWidget',
  },
  currency: {
    baseType: 'string',
    pattern: '^\\d+(\\.\\d{1,2})? [A-Z]{3}$',
    uiWidget: 'CurrencyWidget',
  },
  percentage: {
    baseType: 'number',
    maximum: 100,
    minimum: 0,
    uiWidget: 'PercentageWidget',
  },
  phoneNumber: {
    baseType: 'string',
    pattern: '^[0-9+\\-()]+$',
    uiWidget: 'PhoneNumberWidget',
  },
  multipleChoice: {
    baseType: 'array',
    items: {
      enum: [],
      type: 'string',
    },
    uniqueItems: true,
    uiWidget: 'MultipleChoiceWidget',
  },
  singleChoice: {
    baseType: 'string',
    enum: [],
    uiWidget: 'SingleChoiceWidget',
  },
  string: {
    baseType: 'string',
  },
  boolean: {
    baseType: 'boolean',
  },
  number: {
    baseType: 'number',
  },
}

const NO_INFORMATION_FOUND_OPTION = 'No relevant information detected'

export interface AIFormValueProp {
  type: string
  index: number
  onDelete: () => void
  disabled: boolean
}

export type AIFormValue = {
  title: string
  description: string
  type: string
  index?: number
  pattern?: string
  maximum?: number
  minimum?: number
  choiceOptions?: (string | null)[]
}

export type CrmIntegrationOperationType = 'default' | 'discrete_fields' | 'merged_fields'

interface FormValues {
  formDescription: string
  formName: string
  userLevel: boolean
  includeAdditionalSources: boolean
  formFields: AIFormValue[]
  crmIntegrationOption?: CrmIntegrationOperationType
  selectedCrmField?: string
}

const SupportedIntegrations = ['salesforce', 'practifi', 'xlr8', 'wealthbox', 'redtail']

const FORM_FIELD_COUNT = 30
const CHOICE_FIELD_COUNT = 10

export const EditAIFormView: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const importedForm = useAppSelector(selectImportedForm)

  const { formId = '', activeFormTab = '' } = useParams()
  const [isDisabled, setIsDisabled] = useState<boolean>(false)

  const { hasQuota } = useZocksQuota()
  const documentSummaryEnabled = hasQuota('ai.documentSummaryAgent.value')

  const defaultValues: FormValues = {
    formDescription: '',
    formName: '',
    userLevel: true,
    includeAdditionalSources: false,
    formFields: [],
    crmIntegrationOption: 'default',
  }

  const form = useForm<FormValues>({ defaultValues })
  const formName = form.watch('formName')
  const formFields = form.watch('formFields')

  const crmIntegrationOption = form.watch('crmIntegrationOption')

  const [showCrmMultiFieldModal, setShowCrmMultiFieldModal] = useState(false)

  const [formIntegrationInfos, setFormIntegrationInfos] = useState<AiFeatureIntegrationInfo[] | null>(null)
  const [showIncompleteFieldsModal, setShowIncompleteFieldsModal] = useState(false)
  const [shouldProceedToSave, setShouldProceedToSave] = useState(false)

  const [selectedIntegration, setSelectedIntegration] = React.useState<string | null>(null)

  const handleIntegrationSelect = (serviceId: string) => {
    setSelectedIntegration(serviceId)
    setShowCrmMultiFieldModal(true)
  }

  const [unmappedFieldCount, setUnmappedFieldCount] = useState(0)

  const [scorecard, setScorecard] = useState<Scorecard | null>(null)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [createScorecard] = useCreateScorecardMutation()
  const [updateScorecard] = useUpdateScorecardMutation()
  const navigate = useNavigate()
  const { tracked } = useTracking()

  const [selectedTab, setSelectedTab] = useState<string>(activeFormTab)

  const changeTab = (key: string | null) => {
    if (key) {
      setSelectedTab(key)
      navigate(`/workspace/setup/forms/form/details/${formId}/${key}`)
    }
  }

  const { data: formData, isLoading: isFeatureLoading } = useGetFeatureByIdQuery(
    {
      accountId: accountId,
      aiFeatureId: formId,
    },
    { refetchOnMountOrArgChange: true, skip: !formId },
  )

  const { data: scorecardData, isLoading: isScorecardLoading } = useGetAllScorecardsQuery(
    {
      accountId: accountId,
      aiFeatureIds: formId,
    },
    { refetchOnMountOrArgChange: true, skip: !formId },
  )
  const isLoading = isFeatureLoading || isScorecardLoading

  useEffect(() => {
    if (!formData?.data) {
      return
    }
    form.setValue('userLevel', formData.data.generalConfig?.userLevel ?? true)
    const config = formData.data.config as { [key: string]: any }
    const scorecard = scorecardData && scorecardData.data.find((scorecard) => scorecard.aiFeatureIds[0] === formData.data.id)
    form.setValue('includeAdditionalSources', config?.includeAdditionalSources ?? false)
    const formName = scorecard?.name ?? ''
    form.setValue('formName', formName)
    const isSystem = scorecard?.systemPreset ?? false
    const isManagedByEnterprise = scorecard?.isManagedByEnterprise ?? false

    if (formData.data.integrationInfos && formData.data.integrationInfos[0].fieldConfig) {
      form.setValue('crmIntegrationOption', formData.data.integrationInfos[0].fieldConfig.outputMappingMode)
      setFormIntegrationInfos(formData.data.integrationInfos)
    }

    setIsDisabled(isSystem || isManagedByEnterprise)
  }, [scorecardData, formData, form])

  const inferTypeFromDescriptor = (descriptor: any): string => {
    const uiWidget = descriptor.customWidgets?.['ui:widget']

    const widgetTypeMap: Record<string, string> = {
      USDateInputWidget: 'date',
      SSNInputWidget: 'ssn',
      ITINInputWidget: 'itin',
      CurrencyWidget: 'currency',
      PercentageWidget: 'percentage',
      PhoneNumberWidget: 'phoneNumber',
      SingleChoiceWidget: 'singleChoice',
      MultipleChoiceWidget: 'multipleChoice',
    }

    if (uiWidget && widgetTypeMap[uiWidget]) {
      return widgetTypeMap[uiWidget]
    }
    return descriptor.type || 'string'
  }

  useEffect(() => {
    let scorecard: CreateScorecard | null = null
    if (scorecardData?.data.length && formId) {
      scorecard = scorecardData.data[0]
    } else if (importedForm) {
      scorecard = importedForm
      form.setValue('formName', scorecard.name)
      form.setValue('userLevel', scorecard.config?.userLevel ?? true)
      form.setValue('includeAdditionalSources', scorecard.config?.includeAdditionalSources ?? false)
      if (scorecard.integrationInfos && scorecard.integrationInfos[0].fieldConfig) {
        form.setValue('crmIntegrationOption', scorecard.integrationInfos[0].fieldConfig.outputMappingMode)
        setFormIntegrationInfos(scorecard.integrationInfos)
      }
    } else {
      return
    }

    if (!scorecard.descriptor['properties']) return

    const properties = scorecard.descriptor['properties']
    const customWidgets = scorecard.descriptor['customWidgets'] || {}

    const formFields = Object.entries(properties)
      .map(([key, descriptor]: [string, any]) => {
        const originalType = inferTypeFromDescriptor({
          ...descriptor,
          customWidgets: customWidgets[key],
        })

        const fieldData: any = {
          title: descriptor.title,
          description: descriptor.description,
          type: originalType,
          index: descriptor.index || 0,
        }
        if (originalType === 'singleChoice' && descriptor.enum) {
          fieldData.choiceOptions = descriptor.enum.filter((option: string) => option !== NO_INFORMATION_FOUND_OPTION)
        }
        if (originalType === 'multipleChoice' && descriptor.items?.enum) {
          fieldData.choiceOptions = descriptor.items.enum
        }
        return fieldData
      })
      .sort((a, b) => a.index - b.index)

    form.setValue('formFields', formFields)
    form.setValue('formDescription', scorecard.description)
    setScorecard(scorecard as Scorecard)
  }, [form, formId, scorecardData, importedForm])

  const buildFieldDescriptor = (field: AIFormValue & { index: number }): any => {
    const config = FIELD_TYPE_CONFIG[field.type as keyof typeof FIELD_TYPE_CONFIG]

    const descriptor: any = {
      title: field.title,
      type: config.baseType,
      description: field.description,
      index: field.index,
    }

    if (field.type === 'singleChoice' && field.choiceOptions && field.choiceOptions.length > 0) {
      descriptor.enum = [...field.choiceOptions, ...(field.choiceOptions.includes(NO_INFORMATION_FOUND_OPTION) ? [] : [NO_INFORMATION_FOUND_OPTION])]
      descriptor.uniqueItems = true
    }
    if (field.type === 'multipleChoice' && field.choiceOptions && field.choiceOptions.length > 0) {
      descriptor.items = {
        type: 'string',
        enum: [...field.choiceOptions],
      }
    }

    if ('pattern' in config) {
      descriptor.pattern = config.pattern
    }
    if ('maximum' in config) {
      descriptor.maximum = config.maximum
    }
    if ('minimum' in config) {
      descriptor.minimum = config.minimum
    }

    return {
      descriptor,
      ...('uiWidget' in config && config.uiWidget ? { uiWidget: config.uiWidget } : {}),
    }
  }

  const areAllFieldsFilled = () => {
    if (!formIntegrationInfos) {
      return false
    }
    if (crmIntegrationOption === 'merged_fields' && formIntegrationInfos?.some((info) => info?.fieldConfig && Object.keys(info.fieldConfig).length > 0)) {
      return true
    }

    const allFormFieldTitles = formFields.map((field) => field.title)
    const allMappedValues: string[] = formIntegrationInfos.flatMap((integrationInfo) => {
      const crmFieldMappings = integrationInfo?.fieldConfig?.crmFieldMappings
      if (!crmFieldMappings) return []

      return Object.values(crmFieldMappings).flatMap((objectMappings) =>
        Object.values(objectMappings as Record<string, any>).flatMap(
          (
            mappings: {
              value: string
              type: string
            }[],
          ) => mappings.map((mapping) => mapping.value),
        ),
      )
    })

    const unmappedFields = allFormFieldTitles.filter((title) => !allMappedValues.some((mapped) => toCamelCase(mapped ?? '') === toCamelCase(title)))
    setUnmappedFieldCount(unmappedFields.length)

    return unmappedFields.length === 0
  }

  const handleSaveForm = async (data: FormValues) => {
    if ((crmIntegrationOption === 'merged_fields' || crmIntegrationOption === 'discrete_fields') && !areAllFieldsFilled()) {
      setShowIncompleteFieldsModal(true)
      return
    }

    await saveForm(data)
  }

  const handleConfirmedSave = () => {
    setShowIncompleteFieldsModal(false)
    setShouldProceedToSave(true)
    form.handleSubmit(saveForm)()
  }

  useEffect(() => {
    if (shouldProceedToSave) {
      setShouldProceedToSave(false)
    }
  }, [shouldProceedToSave])

  const getIntegrationInfos = (): AiFeatureIntegrationInfo[] | null => {
    if (crmIntegrationOption === 'default' || !formIntegrationInfos) {
      return null
    }

    if (crmIntegrationOption === 'discrete_fields') {
      return formIntegrationInfos.map((integrationInfo) => {
        const updatedFieldConfig = {
          ...integrationInfo.fieldConfig,
          crmFieldMappings: Object.fromEntries(
            Object.entries(integrationInfo.fieldConfig.crmFieldMappings).map(([objectKey, fieldsByKey]) => [
              objectKey,
              Object.fromEntries(
                Object.entries(fieldsByKey).map(([fieldKey, descriptors]) => [
                  fieldKey,
                  descriptors.map((descriptor) => ({
                    ...descriptor,
                    value: toCamelCase(descriptor.value),
                  })),
                ]),
              ),
            ]),
          ),
        }

        return {
          ...integrationInfo,
          fieldConfig: updatedFieldConfig,
        }
      })
    }

    const mergedFieldDescriptors: FieldDescriptor[] = interleaveWithSeparator(
      formFields.map(
        (field): FieldDescriptor => ({
          value: toCamelCase(field.title),
          type: 'field',
        }),
      ),
      DEFAULT_SEPARATOR,
    )

    return formIntegrationInfos.map((integrationInfo) => {
      const crmFieldMappings = integrationInfo.fieldConfig?.crmFieldMappings
      if (!crmFieldMappings) {
        return integrationInfo
      }
      const mappedFieldMappings = Object.keys(crmFieldMappings).reduce((acc: CRMFieldMappings, objectName) => {
        const objectFields = crmFieldMappings[objectName]
        const objectEntries = Object.entries(objectFields).reduce(
          (
            acc: {
              [key: string]: FieldDescriptor[]
            },
            [fieldKey, fieldValues],
          ) => {
            const camelCasedValues = fieldValues.map((descriptor) => ({
              ...descriptor,
              value: toCamelCase(descriptor.value),
            }))

            const shouldUseMerged = camelCasedValues.some((value) => value.value === 'all')

            acc[fieldKey] = shouldUseMerged ? mergedFieldDescriptors : camelCasedValues

            return acc
          },
          {},
        )
        acc[objectName] = objectEntries
        return acc
      }, {})
      return {
        ...integrationInfo,
        fieldConfig: { ...integrationInfo.fieldConfig, crmFieldMappings: mappedFieldMappings },
      }
    })
  }

  const saveForm = async (data: FormValues) => {
    const uiSchemas: Record<string, object> = {}
    const descriptorBody: Record<string, object> = {}

    formFields.forEach((field, index) => {
      const key = toCamelCase(field.title)
      const { descriptor, uiWidget } = buildFieldDescriptor({
        ...field,
        index,
      })

      descriptorBody[key] = descriptor

      if (uiWidget) {
        uiSchemas[key] = { 'ui:widget': uiWidget }
      }
    })

    const descriptor = {
      type: 'object',
      properties: descriptorBody,
      ...(Object.keys(uiSchemas).length > 0 && { customWidgets: uiSchemas }),
    }

    try {
      setIsUpdating(true)

      if (!formId) {
        await handleFormCreated(data, descriptor, getIntegrationInfos())
      } else {
        await handleFormUpdated(data, descriptor, getIntegrationInfos())
      }

      navigate('/workspace/setup/forms')
    } catch (e) {
      console.error(e)
    }
  }

  const _createScorecard = async (data: FormValues, descriptor: object, integrationInfos: AiFeatureIntegrationInfo[] | null) => {
    await createScorecard({
      accountId: accountId,
      payload: {
        name: data.formName,
        description: data.formDescription,
        descriptor: descriptor,
        type: ScorecardType.FORM,
        config: {
          userLevel: data.userLevel,
          includeAdditionalSources: data.includeAdditionalSources,
        },
        integrationInfos,
      },
    }).unwrap()
  }

  const _updateScorecard = async (data: FormValues, descriptor: object, integrationInfos: AiFeatureIntegrationInfo[] | null) => {
    if (!scorecard) return

    await updateScorecard({
      accountId: accountId,
      scorecardId: scorecard.id,
      payload: {
        name: data.formName,
        description: data.formDescription,
        descriptor: descriptor,
        config: {
          userLevel: data.userLevel,
          includeAdditionalSources: data.includeAdditionalSources,
        },
        integrationInfos,
      },
    }).unwrap()
  }

  const handleFormCreated = tracked(_createScorecard, {
    eventName: TrackEvent.FORM_CREATED,
    eventDetails: {
      formId: scorecard?.id,
      type: crmIntegrationOption,
      serviceId: selectedIntegration,
    },
  })

  const handleFormUpdated = tracked(_updateScorecard, {
    eventName: TrackEvent.FORM_UPDATED,
    eventDetails: {
      formId: scorecard?.id,
    },
  })

  const handleCancel = () => {
    navigate('/workspace/setup/forms')
  }

  const handleOnCrmMappingSave = (crmFieldMappings: CRMFieldMappings) => {
    if (!crmIntegrationOption || crmIntegrationOption === 'default' || !selectedIntegration) {
      return
    }
    const newIntegrationInfo = {
      serviceId: selectedIntegration,
      template: '',
      fieldConfig: {
        outputMappingMode: crmIntegrationOption,
        crmFieldMappings: crmFieldMappings,
      },
    }
    if (!formIntegrationInfos) {
      setFormIntegrationInfos([newIntegrationInfo])
    } else {
      const newFormIntegrationInfos = formIntegrationInfos.filter((info) => info.serviceId !== selectedIntegration)
      newFormIntegrationInfos.push(newIntegrationInfo)
      setFormIntegrationInfos(newFormIntegrationInfos)
    }
    setShowCrmMultiFieldModal(false)
  }

  const handleOnCancel = () => {
    setShowCrmMultiFieldModal(false)
  }

  const FormTemplatesTabTitle = (
    <div className='d-flex align-items-center zocks-gap-0.25'>
      {!documentSummaryEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Document Templates
    </div>
  )

  if (isLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }

  return (
    <FormProvider {...form}>
      <Container>
        <Row className='mb-3'>
          <Col>
            <Breadcrumb>
              <Breadcrumb.Item
                linkProps={{
                  to: '/workspace/setup/forms',
                  className: `${buttonStyles.button} ${buttonStyles.linkButton} text-decoration-underline`,
                }}
                linkAs={NavLink}
              >
                Forms
              </Breadcrumb.Item>
              <Breadcrumb.Item active>{formName}</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
        <Row>
          <Col>
            {formData?.data ? (
              <Tabs onSelect={changeTab} defaultActiveKey={selectedTab} id='tabs' className='d-flex gap-2 mb-4'>
                <Tab eventKey='' title='Details'>
                  <FormTab
                    isDisabled={isDisabled}
                    setFormIntegrationInfos={setFormIntegrationInfos}
                    setSelectedIntegration={setSelectedIntegration}
                    handleIntegrationSelect={handleIntegrationSelect}
                    handleCancel={handleCancel}
                    scorecard={scorecard}
                    handleSaveForm={handleSaveForm}
                    isUpdating={isUpdating}
                  />
                </Tab>
                <Tab eventKey='templates' title={FormTemplatesTabTitle}>
                  <FormTemplatesTab scorecard={scorecard} />
                </Tab>
              </Tabs>
            ) : (
              <FormTab
                isDisabled={isDisabled}
                setFormIntegrationInfos={setFormIntegrationInfos}
                setSelectedIntegration={setSelectedIntegration}
                handleIntegrationSelect={handleIntegrationSelect}
                handleCancel={handleCancel}
                scorecard={scorecard}
                handleSaveForm={handleSaveForm}
                isUpdating={isUpdating}
              />
            )}
          </Col>
        </Row>
      </Container>

      {selectedIntegration ? (
        <CrmMappingMultiFieldModal
          showCrmModal={showCrmMultiFieldModal}
          onSave={handleOnCrmMappingSave}
          onCancel={handleOnCancel}
          connector={selectedIntegration}
          formFields={formFields}
          operationType={crmIntegrationOption}
          integrationFormFields={
            formData?.data?.integrationInfos?.find((info) => info.serviceId === selectedIntegration)?.fieldConfig?.crmFieldMappings ?? null
          }
        />
      ) : null}

      <UnmappedFieldsModal
        show={showIncompleteFieldsModal}
        onHide={() => setShowIncompleteFieldsModal(false)}
        crmIntegrationOption={crmIntegrationOption ?? null}
        unmappedFieldCount={unmappedFieldCount}
        onCompleteMapping={() => {
          setShowIncompleteFieldsModal(false)
          setShowCrmMultiFieldModal(true)
        }}
        onSaveForm={handleConfirmedSave}
      />
    </FormProvider>
  )
}

interface FormTabProps {
  isDisabled: boolean
  setFormIntegrationInfos: React.Dispatch<React.SetStateAction<AiFeatureIntegrationInfo[] | null>>
  setSelectedIntegration: React.Dispatch<React.SetStateAction<string | null>>
  handleIntegrationSelect: (serviceId: string) => void
  handleCancel: () => void
  scorecard: Scorecard | null
  handleSaveForm: (data: FormValues) => Promise<void>
  isUpdating: boolean
}

const FormTab: React.FC<FormTabProps> = ({
  isDisabled,
  setFormIntegrationInfos,
  setSelectedIntegration,
  handleIntegrationSelect,
  handleCancel,
  scorecard,
  handleSaveForm,
  isUpdating,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: currentUser } = useGetMeQuery({ accountId })

  const form = useFormContext<FormValues>()

  const userLevel = form.watch('userLevel')
  const selectedCrmField = form.watch('selectedCrmField')
  const crmIntegrationOption = form.watch('crmIntegrationOption')
  const formFields = form.watch('formFields')

  const handleAddField = () => {
    form.setValue('formFields', [...form.getValues('formFields'), { title: '', description: '', type: 'string' }])
  }

  const handleDeleteField = (index: number) => {
    form.clearErrors(`formFields.${index}`)
    form.setValue(
      'formFields',
      formFields.filter((_, i) => i !== index),
    )
  }

  return (
    <>
      <Row className='d-flex align-items-center gy-3 mb-3'>
        <Col className='d-flex gap-2 m-0 mt-2'>
          <div className='d-flex flex-column flex-grow-1'>
            <div className='paragraph-medium'>Form name:</div>
            <Controller
              render={({ field, fieldState }) => {
                return (
                  <>
                    <Form.Control id={field.name} type='text' {...field} placeholder='Form name' disabled={isDisabled} isInvalid={!!fieldState.error} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )
              }}
              name={`formName`}
              rules={{
                required: 'Form name is required.',
              }}
              control={form.control}
            ></Controller>
          </div>
        </Col>
      </Row>
      <Row>
        <Col className='mb-3'>
          <div className='d-flex gap-2'>
            <div className='paragraph-medium'>Form description:</div>
            <Tooltip className='zocks-text-gray-500' placement='right' title='A short description of the form'>
              <i className='bi bi-info-circle' />
            </Tooltip>
          </div>
          <div className='flex-grow-1 d-flex zocks-form-container'>
            <Controller
              render={({ field }) => {
                return <Form.Control id={field.name} as='textarea' rows={2} {...field} placeholder={'A short description of the form'} disabled={isDisabled} />
              }}
              name={`formDescription`}
              control={form.control}
            ></Controller>
          </div>
        </Col>
      </Row>
      <Row>
        <Col className='mb-3'>
          <div className='d-flex gap-2'>
            <div className='paragraph-medium'>Meeting configuration:</div>
            <Tooltip
              className='zocks-text-gray-500'
              placement='right'
              title='These settings only apply to forms used in meetings and are ignored when you fill in a form from the contact page.'
            >
              <i className='bi bi-info-circle' />
            </Tooltip>
          </div>
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col xs={'auto'}>
          <ScopedBlock
            scope={{
              scope: 'scorecard:update',
              superScope: ['scorecard:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Controller
              name='userLevel'
              control={form.control}
              render={({ field }) => {
                return (
                  <div>
                    <Form.Check
                      inline
                      type='radio'
                      name='userLevel-group'
                      label='Fill out several forms for each client on the meeting'
                      id='userLevel-form-1'
                      checked={field.value === true}
                      onChange={() => field.onChange(true)}
                      disabled={isDisabled}
                    />
                    <Form.Check
                      inline
                      type='radio'
                      name='userLevel-group'
                      label='Fill out one form for everyone on the meeting'
                      id='userLevel-form-2'
                      checked={field.value === false}
                      onChange={() => field.onChange(false)}
                      disabled={isDisabled}
                    />
                  </div>
                )
              }}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col md={6} xs={12}>
          <Divider />
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col xs={'auto'}>
          <ScopedBlock
            scope={{
              scope: 'scorecard:update',
              superScope: ['scorecard:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Controller
              name='includeAdditionalSources'
              control={form.control}
              render={({ field }) => {
                return (
                  <div>
                    <Form.Check
                      inline
                      name='includeAdditionalSources-group'
                      type='radio'
                      label='Only use meeting data to fill out form'
                      id='includeAdditionalSources-form-2'
                      checked={field.value === false}
                      onChange={() => field.onChange(false)}
                      disabled={isDisabled}
                    />
                    {userLevel && (
                      <Form.Check
                        inline
                        name='includeAdditionalSources-group'
                        type='radio'
                        label='Use all available data sources including CRM to fill out form'
                        id='includeAdditionalSources-form-1'
                        checked={field.value === true}
                        onChange={() => field.onChange(true)}
                        disabled={isDisabled}
                      />
                    )}
                  </div>
                )
              }}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row>
        <Col className='mb-3'>
          <div className='d-flex gap-2'>
            <div className='paragraph-medium'>CRM Integration:</div>
            <Tooltip className='zocks-text-gray-500' placement='right' title='Choose how this form should be saved to your CRM system'>
              <i className='bi bi-info-circle' />
            </Tooltip>
          </div>

          <Controller
            name='crmIntegrationOption'
            control={form.control}
            render={({ field }) => (
              <div className='mt-2 d-flex flex-column gap-2'>
                <Form.Check
                  type='radio'
                  label='Save all form data as a note in CRM'
                  id='crmOption-note'
                  value='default'
                  checked={field.value === 'default'}
                  onChange={() => {
                    field.onChange('default')
                    setFormIntegrationInfos(null)
                  }}
                  disabled={isDisabled}
                />
                <Form.Check
                  type='radio'
                  label='Save all form data in one CRM field'
                  id='crmOption-single'
                  value='merged_fields'
                  checked={field.value === 'merged_fields'}
                  onChange={() => field.onChange('merged_fields')}
                  disabled={isDisabled}
                />
                <Form.Check
                  type='radio'
                  label='Map each form field to a specific CRM field'
                  id='crmOption-map'
                  value='discrete_fields'
                  checked={field.value === 'discrete_fields'}
                  onChange={() => field.onChange('discrete_fields')}
                  disabled={isDisabled}
                />
              </div>
            )}
          />

          {crmIntegrationOption === 'merged_fields' && (
            <SelectDropdown
              className='zocks-px-0.75 py-2 inline-block w-auto'
              options={
                currentUser?.integrations
                  ?.filter((integration) => SupportedIntegrations.includes(integration.serviceId))
                  .map((integration) => {
                    return {
                      id: integration.serviceId,
                      label: integration.serviceId,
                      displayLabel: (
                        <div className='zocks-px-0.75 py-2 inline-block w-auto'>
                          <IntegrationIcon serviceId={integration.serviceId as IntegrationServiceId} className='crm-icon' fill='var(--zocks-gray-900)' />
                          <span className='zocks-px-0.75 py-2 inline-block w-auto'>{integration.serviceId}</span>
                        </div>
                      ),
                    }
                  }) ?? []
              }
              label='Select CRM Field'
              onOptionSelected={(option) => {
                setSelectedIntegration(option.id)
                handleIntegrationSelect(option.id)
              }}
              clearDropdownOnOptionSelected
            />
          )}

          {crmIntegrationOption === 'discrete_fields' && (
            <SelectDropdown
              className='zocks-px-0.75 py-2 inline-block w-auto'
              options={
                currentUser?.integrations
                  ?.filter((integration) => SupportedIntegrations.includes(integration.serviceId))
                  .map((integration) => {
                    return {
                      id: integration.serviceId,
                      label: integration.serviceId,
                      displayLabel: (
                        <div>
                          <IntegrationIcon serviceId={integration.serviceId as IntegrationServiceId} className='crm-icon' fill='var(--zocks-gray-900)' />
                          {integration.serviceId}
                        </div>
                      ),
                    }
                  }) ?? []
              }
              label='Configure CRM mapping'
              onOptionSelected={(option) => {
                setSelectedIntegration(option.id)
                handleIntegrationSelect(option.id)
              }}
              clearDropdownOnOptionSelected
              disabled={isDisabled}
            />
          )}

          {selectedCrmField && (
            <div className='text-muted mt-2'>
              Selected: <strong>{selectedCrmField}</strong>
            </div>
          )}
        </Col>
      </Row>

      <Row className='mb-2'>
        <Col>
          <Row className='mb-2 gap-3'>
            <Col className='p-0' xs={2}>
              <div className='d-flex gap-2'>
                <div className='paragraph-medium'>Label</div>
                <Tooltip className='zocks-text-gray-500' placement='right' title='Name of the form field'>
                  <i className='bi bi-info-circle' />
                </Tooltip>
              </div>
            </Col>
            <Col className='p-0' xs={7}>
              <div className='d-flex gap-2'>
                <div className='paragraph-medium'>Description</div>
                <Tooltip className='zocks-text-gray-500' placement='right' title='A concise description about the label'>
                  <i className='bi bi-info-circle' />
                </Tooltip>
              </div>
            </Col>
            <Col className='p-0'>
              <div className='d-flex gap-2'>
                <div className='paragraph-medium'>Type</div>
                <Tooltip className='zocks-text-gray-500' placement='right' title='Type of the field'>
                  <i className='bi bi-info-circle' />
                </Tooltip>
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={1}></Col>
      </Row>
      {formFields
        ? formFields.map((form, index) => <AIFormField disabled={isDisabled} type={form.type} index={index} onDelete={() => handleDeleteField(index)} />)
        : null}
      <Row>
        <Col className='p-0 mt-2'>
          <Button
            className='button-medium w-100 zocks-border-0.25 py-2 zocks-border-dashed zocks-border-color-gray-500'
            onClick={handleAddField}
            variant='link'
            disabled={formFields.length >= FORM_FIELD_COUNT || isDisabled}
          >
            {formFields.length >= FORM_FIELD_COUNT ? <Plus /> : <PlusDark />}
            Add Form Field
          </Button>
        </Col>
      </Row>
      <Row>
        <Col className='d-flex align-items-center p-0 mt-4 justify-content-lg-end gap-3'>
          <Button variant='secondary' onClick={handleCancel}>
            Cancel
          </Button>
          <ScopedBlock
            missingScopeAction={MissingScopeAction.DISABLE}
            scope={{
              scope: scorecard ? 'scorecard:update' : 'scorecard:create',
              superScope: ['scorecard:admin'],
            }}
          >
            <Button variant='primary' onClick={form.handleSubmit(handleSaveForm)} loading={isUpdating} disabled={isDisabled}>
              <i className='bi bi-check-lg' />
              Save
            </Button>
          </ScopedBlock>
        </Col>
      </Row>
    </>
  )
}

export const AIFormField: React.FC<AIFormValueProp> = ({ index, disabled, onDelete }) => {
  const { control, setValue, getValues, watch } = useFormContext()

  const getLabelById = (id: string): string => {
    const option = options.find((option) => option.id === id)
    return option?.label ?? 'text'
  }

  const options = [
    { id: 'string', label: 'text' },
    { id: 'boolean', label: 'yes/no' },
    { id: 'number', label: 'number' },
    { id: 'date', label: 'date' },
    { id: 'currency', label: 'currency' },
    { id: 'percentage', label: 'percentage' },
    { id: 'phoneNumber', label: 'phone number' },
    { id: 'ssn', label: 'SSN' },
    { id: 'itin', label: 'ITIN' },
    { id: 'singleChoice', label: 'single choice' },
    { id: 'multipleChoice', label: 'multiple choice' },
  ]

  const choiceOptions = watch(`formFields.${index}.choiceOptions`) || []
  const displayOptions = choiceOptions.filter((option: string | null) => option !== null)

  const currentType = watch(`formFields.${index}.type`)
  const typeLabel = getLabelById(currentType)

  const addChoiceOption = (value: string) => {
    const currentOptions = getValues(`formFields.${index}.choiceOptions`) || []
    const filteredOptions = currentOptions.filter((option: string | null) => option !== null)
    setValue(`formFields.${index}.choiceOptions`, [...filteredOptions, value])
  }

  const updateChoiceOption = (optionIndex: number, value: string) => {
    const currentOptions = getValues(`formFields.${index}.choiceOptions`) || []
    const filteredOptions = currentOptions.filter((option: string | null) => option !== null)
    const updatedOptions = [...filteredOptions]
    updatedOptions[optionIndex] = value
    setValue(`formFields.${index}.choiceOptions`, updatedOptions)
  }

  const removeChoiceOption = (optionIndex: number) => {
    const currentOptions = getValues(`formFields.${index}.choiceOptions`) || []
    const filteredOptions = currentOptions.filter((option: string | null) => option !== null)
    setValue(
      `formFields.${index}.choiceOptions`,
      filteredOptions.filter((_: string, idx: number) => idx !== optionIndex),
    )
  }

  const handleTypeSelected = (option: SelectOption) => {
    setValue(`formFields.${index}.type`, option.id)
    if (option.id === 'singleChoice' || option.id === 'multipleChoice') {
      setValue(`formFields.${index}.choiceOptions`, [''])
    } else {
      setValue(`formFields.${index}.choiceOptions`, null)
    }
  }

  const keyDownHandler = (evt: React.KeyboardEvent<HTMLElement>, optionIndex: number) => {
    if (evt.key === 'Enter') {
      evt.preventDefault()
      if (optionIndex === choiceOptions.length - 1 && choiceOptions[optionIndex]) {
        addChoiceOption('')
      }
    }
  }

  return (
    <Row className='zocks-bg-color-gray-100 border-2 p-2 rounded-2 mb-2 align-items-center'>
      <Col>
        <Row className='gap-3 align-items-cente'>
          <Col className='p-0' xs={2}>
            <Controller
              name={`formFields.${index}.title`}
              control={control}
              rules={{ required: 'Label is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Form.Control type='text' {...field} placeholder='Label' disabled={disabled} isInvalid={!!fieldState.error} maxLength={64} />
                  <Form.Control.Feedback type='invalid'>{fieldState.error?.message}</Form.Control.Feedback>
                </>
              )}
            />
          </Col>
          <Col className='p-0' xs={7}>
            <Controller
              name={`formFields.${index}.description`}
              control={control}
              rules={{ required: 'Description is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Form.Control type='text' {...field} placeholder='A description of the provided label' disabled={disabled} isInvalid={!!fieldState.error} />
                  <Form.Control.Feedback type='invalid'>{fieldState.error?.message}</Form.Control.Feedback>
                </>
              )}
            />
          </Col>
          <Col className='p-0'>
            <SelectDropdown
              className='zocks-px-0.75 py-2'
              label={typeLabel}
              options={options}
              initialSelectedOption={{
                id: currentType,
                label: getLabelById(currentType),
              }}
              onOptionSelected={handleTypeSelected}
              disabled={disabled}
            />
          </Col>
        </Row>
      </Col>
      <Col xs={1} className='p-0'>
        <Col className='d-flex justify-content-end'>
          <Button variant='darkGhost' className='zocks-p-0.75' onClick={onDelete} disabled={disabled}>
            <i className='bi bi-trash' />
          </Button>
        </Col>
      </Col>
      {(typeLabel === 'single choice' || typeLabel === 'multiple choice') && (
        <div className='p-0 mt-2'>
          <div>
            <Row>
              <Col>
                <div className='fw-semibold mb-2 p-0'>Add options:</div>
              </Col>
            </Row>
            {displayOptions.map((option: string, optionIndex: number) => (
              <Row key={optionIndex} className='mb-2 align-items-center gx-2'>
                <Col xs='auto' style={{ minWidth: '40px' }}>
                  {optionIndex + 1}.
                </Col>
                <Col>
                  <Form.Control
                    type='text'
                    value={option}
                    placeholder='Enter option text'
                    onChange={(e) => updateChoiceOption(optionIndex, e.target.value)}
                    onKeyDown={(e) => keyDownHandler(e, optionIndex)}
                    disabled={disabled}
                  />
                </Col>
                <Col xs='auto'>
                  <Button
                    variant='darkGhost'
                    className='zocks-p-0.75'
                    onClick={() => removeChoiceOption(optionIndex)}
                    disabled={disabled || choiceOptions.length === 1}
                  >
                    <i className='bi bi-trash' />
                  </Button>
                </Col>
              </Row>
            ))}
            <Row className='mt-3 d-flex align-items-center ms-3'>
              <Button
                className='button-medium d-flex align-items-center me-2'
                onClick={() => {
                  addChoiceOption('')
                }}
                variant='link'
                disabled={choiceOptions.length >= CHOICE_FIELD_COUNT || disabled}
              >
                {choiceOptions.length >= CHOICE_FIELD_COUNT ? <Plus /> : <PlusDark />}
                Add Option
              </Button>
            </Row>
          </div>
        </div>
      )}
    </Row>
  )
}
